import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiCall from '../app/API/apiService';
import apiEndpoints from '../app/API/apiEndpoints';
import { toasty } from '../Helper';

const difficultyLevels = ['EASY', 'MEDIUM', 'HARD'];

function QuestionFilters({ open, onClose, onApplyFilters }) {
    const [jobCategories, setJobCategories] = useState([]);
    const [jobSubCategories, setJobSubCategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    const initialFilters = {
        jobCategories: [],
        jobSubCategories: [],
        difficultyLevel: '',
        tags: [],
        skills: []
    };

    const [selectedFilters, setSelectedFilters] = useState(initialFilters);

    useEffect(() => {
        const fetchFilterData = async () => {
            if (!open) return;
            
            setIsLoading(true);
            try {
                const [
                    tagsResponse,
                    skillsResponse,
                    jobCategoryResponse,
                    jobSubCategoryResponse
                ] = await Promise.all([
                    apiCall("get", apiEndpoints.TAGS),
                    apiCall("get", apiEndpoints.SKILLS),
                    apiCall("get", apiEndpoints.JOB_CATEGORY),
                    apiCall("get", apiEndpoints.JOB_SUBCATEGORY)
                ]);

                if (tagsResponse) setAvailableTags(tagsResponse);
                if (skillsResponse) setAvailableSkills(skillsResponse);
                if (jobCategoryResponse) setJobCategories(jobCategoryResponse);
                if (jobSubCategoryResponse) setJobSubCategories(jobSubCategoryResponse);
                
            } catch (error) {
                console.error('Error fetching filter data:', error);
                toasty('Error loading filter options', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchFilterData();
    }, [open]);

    useEffect(() => {
        if (open) {
            setSelectedFilters(initialFilters);
        }
    }, [open]);

    const handleClose = () => {
        setSelectedFilters(initialFilters);
        onClose();
    };

    const handleFilterChange = (field, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        try {
            const params = new URLSearchParams();
            
            if (selectedFilters.tags.length > 0) {
                const tagString = selectedFilters.tags.join(',');
                params.append('tags', tagString);
            }

            if (selectedFilters.skills.length > 0) {
                const skillString = selectedFilters.skills.join(',');
                params.append('skills', skillString);
            }

            if (selectedFilters.difficultyLevel) {
                params.append('difficulty', selectedFilters.difficultyLevel);
            }

            if (selectedFilters.jobCategories.length > 0) {
                const categoryNames = selectedFilters.jobCategories.map(cat => `^${cat.name}$`);
                params.append('jobcategory', categoryNames.join(','));
            }

            if (selectedFilters.jobSubCategories.length > 0) {
                const subCategoryNames = selectedFilters.jobSubCategories.map(subCat => `^${subCat.name}$`);
                params.append('jobsubcategory', subCategoryNames.join(','));
            }

            const queryString = params.toString();
            const response = await apiCall(
                "get",
                `${apiEndpoints.QUESTIONS}/filter${queryString ? `?${queryString}` : ''}`
            );

            if (response) {
                onApplyFilters(response);
                onClose();
            } else {
                toasty('Failed to apply filters', 'error');
            }
        } catch (error) {
            console.error('Error applying filters:', error);
            toasty('Error applying filters', 'error');
        }
    };

    const handleClearFilters = () => {
        setSelectedFilters(initialFilters);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: { width: 320, p: 3 }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Autocomplete
                    multiple
                    value={selectedFilters.jobCategories}
                    onChange={(_, newValue) => handleFilterChange('jobCategories', newValue)}
                    options={jobCategories}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label="Job Categories"
                            disabled={isLoading}
                            placeholder="Select job categories"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.name}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                />

                <Autocomplete
                    multiple
                    value={selectedFilters.jobSubCategories}
                    onChange={(_, newValue) => handleFilterChange('jobSubCategories', newValue)}
                    options={jobSubCategories}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label="Job Sub Categories"
                            disabled={isLoading}
                            placeholder="Select job sub categories"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.name}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                />

                <FormControl fullWidth>
                    <InputLabel>Difficulty Level</InputLabel>
                    <Select
                        value={selectedFilters.difficultyLevel}
                        onChange={(e) => handleFilterChange('difficultyLevel', e.target.value)}
                        label="Difficulty Level"
                    >
                        <MenuItem value="">All</MenuItem>
                        {difficultyLevels.map((level) => (
                            <MenuItem key={level} value={level}>
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    multiple
                    freeSolo
                    value={selectedFilters.tags}
                    onChange={(_, newValue) => handleFilterChange('tags', newValue)}
                    options={availableTags.map(tag => tag.name)}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return [];
                        const filtered = options.filter(option =>
                            option.toLowerCase().includes(inputValue.toLowerCase())
                        );
                        if (inputValue !== '' && !filtered.includes(inputValue)) {
                            filtered.push(inputValue);
                        }
                        return filtered;
                    }}
                    noOptionsText="Press enter to add new tag"
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tags"
                            disabled={isLoading}
                            placeholder="Type to add tags"
                        />
                    )}
                />

                <Autocomplete
                    multiple
                    freeSolo
                    value={selectedFilters.skills}
                    onChange={(_, newValue) => handleFilterChange('skills', newValue)}
                    options={availableSkills.map(skill => skill.name)}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return [];
                        const filtered = options.filter(option =>
                            option.toLowerCase().includes(inputValue.toLowerCase())
                        );
                        if (inputValue !== '' && !filtered.includes(inputValue)) {
                            filtered.push(inputValue);
                        }
                        return filtered;
                    }}
                    noOptionsText="Press enter to add new skill"
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Skills"
                            disabled={isLoading}
                            placeholder="Type to add skills"
                        />
                    )}
                />

                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClearFilters}
                    >
                        Clear All
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApplyFilters}
                        sx={{
                            backgroundColor: '#2c5da7',
                            '&:hover': {
                                backgroundColor: '#1e4c8f',
                            }
                        }}
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
}

export default QuestionFilters;